import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { mapGraphQLErrors } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { Schema, ViewportSchema } from '../../forms/ViewportForm/schema'
import { ViewportAttributeDTO } from '../../types/viewportAttributeDTO'
import { toDTO } from './api/mapper'
import { UPSERT_VIEWPORTS } from './api/mutation'
import { Data, Variables } from './api/types'
import { UseuseUpsertViewports } from './types'

export const useUpsertViewports = (): UseuseUpsertViewports => {
  const { t } = useTranslation('features/globalSetup/viewport', { keyPrefix: 'form' })
  const { enqueueSnackbar } = useSnackbar()
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})

  const { isPending, mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: UPSERT_VIEWPORTS,
    onCompleted: async ({ upsertViewports }: Data): Promise<void> => {
      if (upsertViewports) {
        const { errors } = upsertViewports
        if (errors && errors.length > 0) {
          setErrors(
            mapGraphQLErrors(errors, attribute => attribute.replace('attributes', 'viewports')),
          )
          enqueueSnackbar(t('error'), { variant: 'error' })
        } else {
          setErrors({})
          enqueueSnackbar(t('success'), { variant: 'success' })
        }
      }
    },
    queriesToInvalidate: ['getViewports'],
  })

  const update = (attributes: ViewportSchema[]): void => {
    mutateAsync({
      input: {
        attributes: attributes.map(
          (attribute: ViewportSchema): ViewportAttributeDTO => toDTO(attribute),
        ),
      },
    })
  }

  return {
    errors,
    loading: isPending,
    update,
  }
}
